import { AppHeader } from '@terminal/components/app-header/AppHeader';
import { ChatInput, ChatInputProps } from '@terminal/components/chat';
import { ScenarioInsightsLegacyDrawerRender } from '@terminal/components/insights-legacy-drawer/ScenarioInsightsLegacyDrawerRender';
import { useTouchDevice } from '@terminal/hooks/MediaQueryHook';
import { mobileAlfredPaths } from '@terminal/Root';
import { AppRoute } from '@terminal/routes';
import { appPaths } from '@terminal/routes/app-paths';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useInsightFeedFilters } from '@terminal/stores/use-insight-feed-filters/useInsightFeedFilters';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { useReasonerParams } from '@terminal/views/alfred/research/hooks/useReasonerParams';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { MarketInsightDrawerRender } from '@terminal/views/dashboard/whats-new-widget/components/market-insight-drawer/MarketInsightDrawerRender';
import { Widgets } from '@terminal/views/widgets/Widgets';
import { InsightDrawerShell } from '@terminal/widgets/insight-drawer-shell/InsightDrawerShell';
import { MarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/MarketCatalystDrawer';
import { cloneElement, useEffect } from 'react';
import { useLocation } from 'react-router';
import { checkIsMobile } from '../ProtectedView';
import styles from './View.module.scss';

export interface AccountViewProps {
  config: AppRoute;
  showHeader: boolean;
}

export const View = ({ config, showHeader }: AccountViewProps) => {
  const location = useLocation();
  const isTouchDevice = useTouchDevice();

  const { navigateToConversation } = useChatNavigation();
  const { conversationId } = useReasonerParams();
  const isCatalystGenerationEnabled = useFlags(state =>
    state.getFeatureFlagState('catalyst_generation')
  );

  const followedThemesFlagEnabled = useFlags
    .getState()
    .getFeatureFlagState('followed_themes');
  const { setSelectedWatchlistIds } = useInsightFeedFilters(state => ({
    setSelectedWatchlistIds: state.setSelectedWatchlistIds,
  }));
  const allWatchlists = useWatchlist(state => state.lists);
  const { isFirstTimeEver, setIsFirstTimeEver } = useInsightFeedFilters(
    state => ({
      isFirstTimeEver: state.isFirstTimeEver,
      setIsFirstTimeEver: state.setIsFirstTimeEver,
    })
  );

  const { element } = config;

  const content = cloneElement(element, {
    renderChatInput: (props?: ChatInputProps) => <ChatInput {...props} />,
  });

  const isMobileAlfredPath =
    mobileAlfredPaths.includes(location.pathname) && isTouchDevice;

  useEffect(() => {
    if (checkIsMobile() && location.pathname !== appPaths.alfred) {
      navigateToConversation(conversationId);
    }
  }, [conversationId, isTouchDevice, location.pathname]);

  // HOTFIX: https://toggleai.atlassian.net/browse/PLATFORM-4325
  useEffect(() => {
    if (followedThemesFlagEnabled && isFirstTimeEver) {
      const ids = allWatchlists.map(list => list.id);
      if (ids.length > 0) {
        setSelectedWatchlistIds(new Set(ids));
      }
      setIsFirstTimeEver(false);
    }
  }, [allWatchlists, followedThemesFlagEnabled, isFirstTimeEver]);

  return (
    <>
      {showHeader && !isMobileAlfredPath && <AppHeader />}
      <div className={styles.viewRoot} id={'root-view'}>
        {!isMobileAlfredPath && (
          <>
            <Widgets />
            <InsightDrawerShell />
            <ScenarioInsightsLegacyDrawerRender />
            <MarketInsightDrawerRender />
            {!isCatalystGenerationEnabled && <MarketCatalystDrawer />}
          </>
        )}
        {content}
      </div>
    </>
  );
};
